/**
 * NotificationCenter.jsx — Rê Leve
 *
 * Central de notificações da Janela do Operador. Renderiza toasts no
 * canto inferior direito quando recebe eventos do liveSyncEngine.
 *
 * Eventos com notificação visual:
 *   - order.closed         → 🎉 verde · "Novo pedido"
 *   - approval.pending     → 📥 mostarda · "Léve consultando você"
 *   - approval.answered    → ✅ verde · "Resposta enviada"
 *   - order.upsell.added   → ⬆ ouro · "Upsell aceito"
 *   - order.cross_sell.added → 🔁 lilás · "Cross-sell aceito"
 *
 * Auto-dismiss em 6s. Som opcional (off por padrão).
 */

import { useEffect, useState } from 'react';
import { subscribe, EVT } from '../lib/liveSyncEngine';

const TOAST_DURATION = 6000;

function buildToast(type, payload) {
  switch (type) {
    case EVT.ORDER_CLOSED:
      return {
        icon: '🎉',
        title: 'Novo pedido fechado!',
        body: `${payload.customerName || 'Cliente'} confirmou ${payload.kitName} · ${formatBRL(payload.total)}`,
        color: 'green',
      };
    case EVT.ORDER_UPSELL_ADDED:
      return {
        icon: '⬆',
        title: 'Upsell aceito',
        body: `${payload.customerName} adicionou ${payload.item} · +${formatBRL(payload.price)}`,
        color: 'mostard',
      };
    case EVT.ORDER_CROSS_SELL_ADDED:
      return {
        icon: '🔁',
        title: 'Cross-sell aceito',
        body: `${payload.customerName} interessou-se em ${payload.suggestedItem}`,
        color: 'lilac',
      };
    case EVT.APPROVAL_PENDING:
      return {
        icon: '📥',
        title: 'Léve consultando você',
        body: `${payload.customerName}: "${truncate(payload.question, 60)}"`,
        color: 'tomate',
      };
    case EVT.APPROVAL_ANSWERED:
      return {
        icon: '✅',
        title: 'Resposta enviada ao cliente',
        body: `Para ${payload.customerName}: ${truncate(payload.answer, 60)}`,
        color: 'green',
      };
    default:
      return null;
  }
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n) + '…' : s;
}
function formatBRL(v) {
  return (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

export default function NotificationCenter() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const subs = [
      EVT.ORDER_CLOSED,
      EVT.ORDER_UPSELL_ADDED,
      EVT.ORDER_CROSS_SELL_ADDED,
      EVT.APPROVAL_PENDING,
      EVT.APPROVAL_ANSWERED,
    ].map(type => subscribe(type, (evt) => {
      const t = buildToast(type, evt.payload || {});
      if (!t) return;
      const id = evt.id || ('toast-' + Date.now() + '-' + Math.random());
      setToasts(prev => [...prev, { id, ...t, ts: evt.ts }]);
      setTimeout(() => {
        setToasts(prev => prev.filter(x => x.id !== id));
      }, TOAST_DURATION);
    }));
    return () => subs.forEach(unsub => unsub());
  }, []);

  function dismiss(id) {
    setToasts(prev => prev.filter(x => x.id !== id));
  }

  return (
    <div className="rl-toast-stack" aria-live="polite" aria-atomic="true">
      {toasts.map(t => (
        <div key={t.id} className={'rl-toast rl-toast--' + t.color}>
          <div className="rl-toast-icon">{t.icon}</div>
          <div className="rl-toast-body">
            <div className="rl-toast-title">{t.title}</div>
            <div className="rl-toast-text">{t.body}</div>
          </div>
          <button className="rl-toast-close" onClick={() => dismiss(t.id)} aria-label="Fechar">×</button>
        </div>
      ))}
    </div>
  );
}
